import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  AdvancedDisclosure,
  Button,
  Dialog,
  ErrorSummary,
  Field,
  FormLayout,
  FormLayoutRow,
  FormLayoutSection,
  Input,
  Select,
} from './common'
import CodeField from './common/data/CodeField'
import styles from './ResourceForm.module.css'

const resourceTypes = [
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'redis', label: 'Redis' },
  { value: 'rest_api', label: 'REST API' },
  { value: 'graphql', label: 'GraphQL' },
  { value: 'gcs', label: 'Google Cloud Storage' },
]

const defaultConfigs = {
  postgresql: { host: '', port: 5432, database: '', username: '', password: '', ssl_mode: 'disable' },
  redis: { host: '', port: 6379, password: '', database: 0 },
  rest_api: { base_url: '', api_key: '', headers: {} },
  graphql: { endpoint: '', api_key: '', headers: {} },
  gcs: { bucket_name: '', service_account_json: '' },
}

const secretFields = new Set(['password', 'api_key', 'auth_token', 'service_account_json'])

function jsonObjectError(value) {
  if (!String(value || '').trim()) return null
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? null
      : 'Enter a JSON object with named fields.'
  } catch (error) {
    return `Invalid JSON: ${error.message}`
  }
}

function JsonObjectCodeField({ value, onChange, resetKey, ...props }) {
  const serializedValue = JSON.stringify(value || {}, null, 2)
  const [draft, setDraft] = useState(serializedValue)

  useEffect(() => {
    setDraft(serializedValue)
  }, [resetKey, serializedValue])

  const handleChange = (nextValue) => {
    setDraft(nextValue)
    if (!nextValue.trim()) {
      onChange({})
      return
    }
    try {
      const parsed = JSON.parse(nextValue)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) onChange(parsed)
    } catch {
      // Keep the editable draft visible while preserving the last valid object.
    }
  }

  return <CodeField {...props} value={draft} onChange={handleChange} language="json" validate={jsonObjectError} />
}

function initialForm(resource) {
  if (!resource) return { name: '', type: '', config: {} }
  return {
    name: resource.name || '',
    type: resource.type || '',
    config: Object.fromEntries(
      Object.entries(resource.config || {}).map(([key, value]) => [key, secretFields.has(key) ? '' : value])
    ),
  }
}

export function ResourceForm({ open, resource, onSubmit, onClose, title }) {
  const formId = useId()
  const errorSummaryRef = useRef(null)
  const [formData, setFormData] = useState(() => initialForm(resource))
  const [errors, setErrors] = useState({})
  const [codeErrors, setCodeErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setFormData(initialForm(resource))
    setErrors({})
    setCodeErrors({})
  }, [resource, open])

  const dirty = useMemo(() => (
    JSON.stringify(formData) !== JSON.stringify(initialForm(resource))
  ), [formData, resource])

  const handleTypeChange = (type) => {
    setFormData((current) => ({ ...current, type, config: { ...(defaultConfigs[type] || {}) } }))
    setErrors({})
    setCodeErrors({})
  }

  const handleCodeValidation = useCallback((field, error) => {
    setCodeErrors((current) => {
      if (current[field] === error) return current
      if (!error) {
        const next = { ...current }
        delete next[field]
        return next
      }
      return { ...current, [field]: error }
    })
    if (!error) {
      setErrors((current) => {
        if (!current[field]) return current
        const next = { ...current }
        delete next[field]
        return next
      })
    }
  }, [])

  const handleConfigChange = (field, value) => {
    setFormData((current) => ({
      ...current,
      config: { ...current.config, [field]: value },
    }))
  }

  const validate = () => {
    const next = {}
    if (!formData.name.trim()) next.name = 'Resource name is required'
    if (!formData.type) next.type = 'Resource type is required'
    if (formData.type === 'postgresql') {
      if (!formData.config.host) next.host = 'Host is required'
      if (!formData.config.database) next.database = 'Database is required'
      if (!formData.config.username) next.username = 'Username is required'
    }
    if (formData.type === 'redis' && !formData.config.host) next.host = 'Host is required'
    if ((formData.type === 'rest_api' || formData.type === 'graphql') && !formData.config.base_url && !formData.config.endpoint) {
      next.url = 'URL is required'
    }
    Object.assign(next, codeErrors)
    setErrors(next)
    if (Object.keys(next).length > 0) requestAnimationFrame(() => errorSummaryRef.current?.focus())
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!validate()) return

    setIsSubmitting(true)
    try {
      await onSubmit({
        ...formData,
        config: {
          ...formData.config,
          port: formData.config.port ? Number.parseInt(formData.config.port, 10) : undefined,
          database: formData.type === 'redis'
            ? Number.parseInt(formData.config.database || 0, 10)
            : formData.config.database,
        },
      })
    } catch {
      // The owning page presents the server error and keeps this dialog open.
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      title={title}
      description={resource ? 'Stored secret values remain hidden. Enter a replacement only when it should change.' : 'Choose a connection type and enter its required details.'}
      onClose={onClose}
      size="large"
      contentClassName={styles.dialogContent}
      footer={(
        <>
          {dirty ? <span className={styles.dirtyState} role="status">Unsaved changes</span> : null}
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" form={formId} loading={isSubmitting}>
            {resource ? 'Update' : 'Create'}
          </Button>
        </>
      )}
    >
      <FormLayout className={styles.form}>
        <form id={formId} className={styles.formBody} onSubmit={handleSubmit} noValidate>
          {Object.keys(errors).length > 0 ? (
            <ErrorSummary ref={errorSummaryRef}>
              <span>Review the required connection fields below.</span>
            </ErrorSummary>
          ) : null}

          <FormLayoutSection title="Resource">
            <Field label="Resource Name" error={errors.name} required>
              <Input
                value={formData.name}
                onChange={(event) => setFormData((current) => ({ ...current, name: event.target.value }))}
                autoComplete="off"
              />
            </Field>
            <Field label="Resource Type" error={errors.type} required>
              <Select value={formData.type} onChange={(event) => handleTypeChange(event.target.value)}>
                <option value="">Select a type</option>
                {resourceTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
              </Select>
            </Field>
          </FormLayoutSection>

          {formData.type ? (
            <FormLayoutSection title="Connection">
              <ResourceConfigFields
                type={formData.type}
                config={formData.config}
                errors={errors}
                onChange={handleConfigChange}
                onCodeValidation={handleCodeValidation}
                resetKey={`${resource?.id || 'new'}-${open}`}
              />
            </FormLayoutSection>
          ) : null}
        </form>
      </FormLayout>
    </Dialog>
  )
}

function ResourceConfigFields({ type, config, errors, onChange, onCodeValidation, resetKey }) {
  if (type === 'postgresql') {
    return (
      <>
        <FormLayoutRow columns={2}>
          <ConfigField label="Host" field="host" value={config.host} error={errors.host} onChange={onChange} required />
          <ConfigField label="Port" field="port" type="number" value={config.port ?? 5432} onChange={onChange} />
        </FormLayoutRow>
        <ConfigField label="Database" field="database" value={config.database} error={errors.database} onChange={onChange} required />
        <FormLayoutRow columns={2}>
          <ConfigField label="Username" field="username" value={config.username} error={errors.username} onChange={onChange} required />
          <ConfigField label="Password" field="password" type="password" value={config.password} onChange={onChange} optional autoComplete="new-password" />
        </FormLayoutRow>
        <Field label="SSL Mode">
          <Select value={config.ssl_mode || 'disable'} onChange={(event) => onChange('ssl_mode', event.target.value)}>
            <option value="disable">Disable</option>
            <option value="require">Require</option>
            <option value="verify-ca">Verify CA</option>
            <option value="verify-full">Verify Full</option>
          </Select>
        </Field>
      </>
    )
  }

  if (type === 'redis') {
    return (
      <>
        <FormLayoutRow columns={2}>
          <ConfigField label="Host" field="host" value={config.host} error={errors.host} onChange={onChange} required />
          <ConfigField label="Port" field="port" type="number" value={config.port ?? 6379} onChange={onChange} />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <ConfigField label="Password" field="password" type="password" value={config.password} onChange={onChange} optional autoComplete="new-password" />
          <ConfigField label="Database Number" field="database" type="number" value={config.database ?? 0} onChange={onChange} />
        </FormLayoutRow>
      </>
    )
  }

  if (type === 'rest_api' || type === 'graphql') {
    const urlField = type === 'rest_api' ? 'base_url' : 'endpoint'
    return (
      <>
        <ConfigField
          label={type === 'rest_api' ? 'Base URL' : 'Endpoint'}
          field={urlField}
          type="url"
          value={config[urlField]}
          error={errors.url}
          onChange={onChange}
          required
        />
        <ConfigField label="API Key" field="api_key" type="password" value={config.api_key} onChange={onChange} optional autoComplete="new-password" />
        <AdvancedDisclosure label="Advanced headers">
          <JsonObjectCodeField
            label="Headers (JSON)"
            help="JSON object sent with each request."
            rows={5}
            value={config.headers}
            onChange={(value) => onChange('headers', value)}
            onValidationChange={(error) => onCodeValidation('headers', error)}
            resetKey={`${resetKey}-${type}`}
          />
        </AdvancedDisclosure>
      </>
    )
  }

  if (type === 'gcs') {
    return (
      <>
        <ConfigField label="Bucket Name" field="bucket_name" value={config.bucket_name} onChange={onChange} />
        <AdvancedDisclosure label="Advanced credentials">
          <CodeField
            label="Service Account JSON"
            language="json"
            validate={jsonObjectError}
            help="Stored credential values are never shown after saving."
            rows={8}
            value={config.service_account_json || ''}
            onChange={(value) => onChange('service_account_json', value)}
            onValidationChange={(error) => onCodeValidation('service_account_json', error)}
            autoComplete="off"
          />
        </AdvancedDisclosure>
      </>
    )
  }

  return null
}

function ConfigField({ label, field, value, onChange, error, type = 'text', ...fieldProps }) {
  return (
    <Field label={label} error={error} {...fieldProps}>
      <Input type={type} value={value ?? ''} onChange={(event) => onChange(field, event.target.value)} />
    </Field>
  )
}
